/* eslint-disable tsdoc/syntax */
import { useRef, useEffect, useState } from "react";
import { Box, IconButton, Modal, Tooltip } from "@mui/material";
import { Close } from "@mui/icons-material";
import { ButtonComponent } from "@zenith/components";
import BasketErrorIcon from "@zenith/assets/icons/BasketErrorIcon";
import { SalesItem } from "../../Payments/interfaces";

export type StockErrorItem = {
  name: string;
  image: string | undefined;
  statusText: string;
  statusType: "out_of_stock" | "limited" | "reserved";
};

export type StockApiDataItem = {
  variantProductId: string;
  productName: string;
  totalStock: string;
  totalReserved: number;
  availableStock: number;
  requestedQuantity: number;
};

export const parseStockErrorFromApiData = (
  data: StockApiDataItem[],
  cartItems: { name: string; image: string | undefined }[]
): StockErrorItem[] => {
  return data.map((item) => {
    let statusText: string;
    let statusType: StockErrorItem["statusType"];

    if (item.availableStock === 0) {
      statusText = "Out of Stock";
      statusType = "out_of_stock";
    } else if (item.totalReserved > 0) {
      statusText = `${item.totalReserved} Item Reserved`;
      statusType = "reserved";
    } else {
      statusText = `Only ${item.availableStock} Available`;
      statusType = "limited";
    }

    const cartItem = cartItems.find(
      (c) => c.name.toLowerCase() === item.productName.toLowerCase()
    );

    return {
      name: item.productName,
      image: cartItem?.image,
      statusText,
      statusType,
    };
  });
};

interface StockPrecheckErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorItems: StockErrorItem[];
}

export const parseStockError = (
  message: string,
  orderItems: { name: string; image: string | undefined }[]
): StockErrorItem[] => {
  const items: StockErrorItem[] = [];
  const regex =
    /Insufficient stock for product "([^"]+)"\. Required: \d+, Available: (\d+), Already reserved: (\d+)/g;
  let match;
  while ((match = regex.exec(message)) !== null) {
    const name = match[1].trim();
    const available = Number.parseInt(match[2], 10);
    const reserved = Number.parseInt(match[3], 10);

    let statusText: string;
    let statusType: StockErrorItem["statusType"];

    if (available === 0) {
      statusText = "Out of Stock";
      statusType = "out_of_stock";
    } else if (reserved > 0) {
      statusText = `${reserved} Item Reserved`;
      statusType = "reserved";
    } else {
      statusText = `Only ${available} Available`;
      statusType = "limited";
    }

    const orderItem = orderItems.find(
      (item) => item.name.toLowerCase() === name.toLowerCase()
    );
    items.push({ name, image: orderItem?.image, statusText, statusType });
  }
  return items;
};

const resolveStatusFromText = (
  detail: string,
  cartItem: SalesItem | undefined
): { statusText: string; statusType: StockErrorItem["statusType"] } => {
  const limitedMatch = /only\s+(\d+)\s+available/.exec(detail);
  const reservedTextMatch =
    /(\d+)\s+(?:units?\s+)?(?:already\s+)?reserved/.exec(detail);

  if (reservedTextMatch) {
    const count = Number.parseInt(reservedTextMatch[1], 10);
    return {
      statusText: `${count} ${count === 1 ? "Item" : "Items"} Reserved`,
      statusType: "reserved",
    };
  }
  if (limitedMatch) {
    return {
      statusText: `Only ${limitedMatch[1]} Available`,
      statusType: "limited",
    };
  }
  if (cartItem) {
    return cartItem.stockAvailable <= 0
      ? { statusText: "Out of Stock", statusType: "out_of_stock" }
      : {
          statusText: `Only ${cartItem.stockAvailable} Available`,
          statusType: "limited",
        };
  }
  return { statusText: "Out of Stock", statusType: "out_of_stock" };
};

const resolveStockStatus = (
  detail: string,
  available: number,
  reserved: number,
  cartItem: SalesItem | undefined
): { statusText: string; statusType: StockErrorItem["statusType"] } => {
  if (available === 0 || (available < 0 && detail.includes("out of stock"))) {
    return { statusText: "Out of Stock", statusType: "out_of_stock" };
  }
  if (reserved > 0) {
    return {
      statusText: `${reserved} ${reserved === 1 ? "Item" : "Items"} Reserved`,
      statusType: "reserved",
    };
  }
  if (available > 0) {
    return { statusText: `Only ${available} Available`, statusType: "limited" };
  }
  return resolveStatusFromText(detail, cartItem);
};

export const parseStockErrorItems = (
  message: string,
  salesSummary: SalesItem[]
): StockErrorItem[] => {
  const items: StockErrorItem[] = [];
  const regex = /\[([^\]]+)\]\s*[→-]+\s*([^\n[]+)/g;
  let match;

  while ((match = regex.exec(message)) !== null) {
    const name = match[1].trim();
    const detail = match[2].trim().toLowerCase().replace(/\.$/, "");
    const cartItem = salesSummary.find(
      (item) => item.name.toLowerCase() === name.toLowerCase()
    );
    const availableMatch = /available[:\s]+(\d+)/.exec(detail);
    const alreadyReservedMatch = /already reserved[:\s]+(\d+)/.exec(detail);
    const available = availableMatch
      ? Number.parseInt(availableMatch[1], 10)
      : -1;
    const reserved = alreadyReservedMatch
      ? Number.parseInt(alreadyReservedMatch[1], 10)
      : -1;
    const { statusText, statusType } = resolveStockStatus(
      detail,
      available,
      reserved,
      cartItem
    );
    items.push({ name, image: cartItem?.image, statusText, statusType });
  }

  return items;
};

const statusStyles: Record<StockErrorItem["statusType"], string> = {
  out_of_stock: "text-[#DA291C]",
  limited: "text-[#AFAFAE]",
  reserved: "text-[#DC890F]",
};

const TruncatedTooltip = ({ text }: { text: string }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.offsetWidth);
  }, [text]);

  return (
    <Tooltip
      title={isTruncated ? text : ""}
      placement="top"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#1A1C1F",
            fontSize: "0.75rem",
            "& .MuiTooltip-arrow": { color: "#1A1C1F" },
          },
        },
      }}
    >
      <p
        ref={ref}
        className="truncate text-[14px] font-medium text-neutral-600"
      >
        {text}
      </p>
    </Tooltip>
  );
};

const StockPrecheckErrorModal = ({
  isOpen,
  onClose,
  errorItems,
}: StockPrecheckErrorModalProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const updateThumb = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setThumbHeight((clientHeight / scrollHeight) * clientHeight);
    setThumbTop((scrollTop / scrollHeight) * clientHeight);
  };

  useEffect(() => {
    if (!isOpen) return;
    const el = scrollRef.current;
    if (!el) return;
    updateThumb();
  }, [errorItems, isOpen]);

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = scrollRef.current?.scrollTop ?? 0;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !scrollRef.current) return;
      const { scrollHeight, clientHeight } = scrollRef.current;
      const delta = ev.clientY - dragStartY.current;
      scrollRef.current.scrollTop =
        dragStartScrollTop.current + delta * (scrollHeight / clientHeight);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const needsScroll = errorItems.length > 4;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="stock-error-modal-title"
    >
      <Box className="absolute top-1/2 left-1/2 flex w-11/12 max-w-[520px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[20px] bg-white outline-none">
        <div className="flex justify-end px-4 pt-4">
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </div>

        <div className="flex flex-col items-center px-14 pb-6">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full text-primary-300">
            <BasketErrorIcon />
          </div>

          <h2
            id="stock-error-modal-title"
            className="mb-2 text-center text-[20px] font-[500] text-[#1A1C1F]"
          >
            We could not process your order
          </h2>
          <p className="mb-4 text-center text-sm text-neutral-600">
            Some of the Items you selected do not have stock available in them
            so we could not process your order.
          </p>

          {errorItems.length > 0 && (
            <>
              <p className="mb-3 text-sm font-medium text-neutral-700">
                Here are the Items:
              </p>
              <div
                className={`relative mb-4 w-full ${needsScroll ? "pr-3" : ""}`}
              >
                <style>{`.stock-scroll::-webkit-scrollbar { display: none; }`}</style>
                <div
                  ref={scrollRef}
                  onScroll={updateThumb}
                  className={`stock-scroll w-full ${
                    needsScroll ? "max-h-[130px] overflow-y-auto" : ""
                  }`}
                  style={{ scrollbarWidth: "none" }}
                >
                  <div
                    className={`grid w-full gap-1 ${
                      errorItems.length === 1 ? "grid-cols-1" : "grid-cols-2"
                    }`}
                  >
                    {errorItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex min-w-0 items-center gap-2 rounded-lg p-1"
                      >
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="h-12 w-12 flex-shrink-0 rounded-lg object-contain"
                            style={{ background: "#F8F8F8" }}
                          />
                        ) : (
                          <div
                            className="h-12 w-12 flex-shrink-0 rounded-lg"
                            style={{ background: "#F8F8F8" }}
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <TruncatedTooltip text={item.name} />
                          <p
                            className={`text-xs ${
                              statusStyles[item.statusType]
                            }`}
                          >
                            {item.statusText}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {needsScroll && (
                  <div className="absolute right-0 top-0 h-full w-1.5 rounded-full bg-neutral-100">
                    <div
                      className="absolute right-0 w-1.5 cursor-grab rounded-full bg-neutral-400 active:cursor-grabbing"
                      style={{ top: thumbTop, height: thumbHeight }}
                      onMouseDown={handleThumbMouseDown}
                    />
                  </div>
                )}
              </div>
              <p className="mb-6 text-center text-sm text-neutral-600">
                Kindly Adjust your cart to continue
              </p>
            </>
          )}
        </div>
        <div className="mb-6 px-6">
          <ButtonComponent
            label="Back to Cart"
            onClick={onClose}
            id="stock-error-back-to-cart"
            size="large"
            type="primary"
            fullWidth
          />
        </div>
      </Box>
    </Modal>
  );
};

export default StockPrecheckErrorModal;
